/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { Box, Center, Container, ProgressCircle, Stack, VStack } from 'chakra-v3';
import { useParams } from 'react-router-dom';
import useAsyncData from '../../utils/AsyncData';
import { models } from '../../service/db/Setup';
import { useCallback, useEffect, useRef, useState } from 'react';
// FIXME: import the correct header here
import MinimalHeader from '../../components/headers/MinimalHeader';
import { debounce } from 'lodash';
import Tutorial from '../../components/TaskTutorial';
import { evalsTutorialSteps } from '@/temp/TaskMock';
import { Text } from '@/components/Text';
import UserHeader from '@/components/headers/UserHeader';
import { MainTaskPhase } from './MainTaskPhase';

function Task() {
  const { taskId } = useParams<{ taskId: string }>();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [tutorialPending, setTutorialPending] = useState(true);

  const activeWorker = useAsyncData(() =>
    models.worker
      .getActiveWorker()
      .unwrapResult()
      // throw error if task not found. Ensures the error object is set in the hook
      .getOrThrow(),
  );

  const task = useAsyncData(
    () =>
      models.basic
        .getSingle('task', taskId!)
        .unwrapResult()
        // throw error if no active worker. Ensures the error object is set in the hook
        .getOrThrow(),
    {
      skip: !taskId,
      deps: [taskId],
    },
  );

  const activeAvatar = useAsyncData(
    () =>
      models.basic
        .getAllFromIndex('avatar', 'task_id', taskId!)
        .unwrapResult()
        // find first avatar for active worker
        .map((avatars) => avatars.find((avatar) => avatar.worker_id === activeWorker.data?.id))
        // throw error if no active avatar. Ensures the error object is set in the hook
        .getOrThrow(),
    {
      skip: !taskId || !activeWorker.data?.id,
      deps: [activeWorker.data?.id, taskId],
    },
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollToBottom = useCallback(
    debounce(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 50),
    [],
  );

  // Disable loading screen after first load
  useEffect(() => {
    if (!firstLoad) {
      scrollToBottom();
      return;
    }
    if (activeAvatar.isLoading || activeWorker.isLoading || task.isLoading) {
      return;
    }
    setFirstLoad(false);
  }, [activeAvatar.isLoading, activeWorker.isLoading, firstLoad, scrollToBottom, task.isLoading]);

  const dataLoadError = activeWorker.error || task.error || activeAvatar.error;

  if (dataLoadError) {
    console.error('Error loading task data:', dataLoadError);
    return <div>Error loading task data: {dataLoadError.message}</div>;
  }

  if (activeAvatar.data === null && !activeAvatar.isLoading) {
    return (
      <VStack h="100vh" bg="bg.primary" w="100%" overflowY="auto" align="stretch">
        <MinimalHeader />
        <Center height="100%" width="100%" textAlign="center">
          No avatar found for the active worker in this task.
        </Center>
      </VStack>
    );
  }

  if (firstLoad) {
    return (
      <Center height="100vh" bg="bg.primary" color="text.primary">
        <ProgressCircle.Root size="lg" mb={4} gap={5} alignItems="center" value={null}>
          <ProgressCircle.Circle>
            <ProgressCircle.Track />
            <ProgressCircle.Range />
          </ProgressCircle.Circle>

          <ProgressCircle.Label asChild>
            <Text variant="heroHeading">Loading the task</Text>
          </ProgressCircle.Label>
        </ProgressCircle.Root>
      </Center>
    );
  }

  return (
    <VStack
      ref={chatContainerRef}
      h="100vh"
      bg="bg.primary"
      w="100%"
      overflowY="auto"
      align="stretch"
      scrollBehavior="smooth"
    >
      <UserHeader />
      <Container maxW="container.md" py="16px" flex="1">
        <Stack align="center" py="24px" px="16px" bg="bg.secondary" boxShadow="sm">
          <Text variant="heroHeading">Welcome to {task.data?.name}</Text>
          <Text variant="heroSubheading">How to do this task</Text>
        </Stack>
        <Tutorial
          steps={evalsTutorialSteps}
          youtubeLink="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
          pdfLink=""
          showStartButton={tutorialPending}
          onStart={() => setTutorialPending(false)}
        />
        <Box marginTop="100px" />
        {!tutorialPending && task.data && activeWorker.data && activeAvatar.data && (
          <MainTaskPhase
            task={task.data}
            activeAvatar={activeAvatar.data}
            activeWorker={activeWorker.data}
            scrollToBottom={scrollToBottom}
          />
        )}
      </Container>
    </VStack>
  );
}

export default Task;
